/**
 * Single source of truth for the content sections.
 *
 * Adding a new section: append here, add a route in App.tsx, add the matching
 * `[data-section='<id>']` rule in index.css, and the rest of the UI picks it
 * up automatically (nav, home grid, footer accent).
 *
 * **Infographics was folded into Notes on 2026-08-10.** A graphic is a thing
 * inside a piece of writing, not a category beside it, so there is no section
 * for visuals: a note carries its own infographic, some other visual, or
 * nothing, decided per note. Infographics here means a browser-compatible
 * interactive artifact — self-contained client-side HTML, the constraint the
 * whole site already runs under.
 *
 * The one live artifact under the old section, a year heatmap on demo data, was
 * retired rather than rehomed, on the same reasoning: it was a visual with no
 * writing around it, which is exactly the thing this section no longer wants to
 * have a home for. `/infographics` and `/infographics/year` now 404.
 */

/*
 * **Render was added on 2026-09-30** as `05`, after notes rather than beside
 * games, so no existing number moves. It holds light-transport renderers —
 * path tracers and their relatives — which are instruments for light the way
 * `/sound` holds instruments for air, not one-screen toys. Strange Solids
 * stays in games: it is a raymarched fractal, not a light simulation.
 */

export type SectionId = 'games' | 'sound' | 'chess' | 'notes' | 'render'

export interface Section {
  id: SectionId
  number: string
  label: string
  blurb: string
  href: string
  accentVar: string
}

export const SECTIONS: Section[] = [
  {
    id: 'games',
    number: '01',
    label: 'Games',
    blurb: 'Small interactive things to fiddle with.',
    href: '/games',
    accentVar: 'var(--color-accent-games)',
  },
  {
    id: 'sound',
    number: '02',
    label: 'Sound',
    blurb: 'Instruments, built from oscillators up.',
    href: '/sound',
    accentVar: 'var(--color-accent-sound)',
  },
  {
    id: 'chess',
    number: '03',
    label: 'Chess',
    blurb: 'Games, puzzles, openings, the rabbit hole.',
    href: '/chess',
    accentVar: 'var(--color-accent-chess)',
  },
  {
    id: 'notes',
    number: '04',
    label: 'Notes',
    blurb: 'Writing, with whatever it needs drawn beside it.',
    href: '/notes',
    accentVar: 'var(--color-accent-notes)',
  },
  {
    id: 'render',
    number: '05',
    label: 'Render',
    blurb: 'Light, simulated — ray and path tracers on the GPU.',
    href: '/render',
    accentVar: 'var(--color-accent-render)',
  },
]

export const findSection = (id: SectionId): Section =>
  SECTIONS.find((s) => s.id === id) ?? SECTIONS[0]
